import {
	Box3,
	Box3Helper,
	Color,
	MeshStandardMaterial,
	Object3D,
	Vector3,
} from 'three';
import { ProjectRenderer } from '../ProjectRenderer';
import { UUID } from '@/types/types';
import { RenderUtils } from '../utils/RenderUtils';
import { ThreeEvent } from '../types';

export abstract class CollidableObject {
	id: UUID;
	collisionBox = new Box3();
	linkedObjectId: UUID | null = null;
	protected object: Object3D;
	protected collisionBoxHelper: Box3Helper;
	protected _isColliding = false;
	protected _posY: number;
	protected dimensions = new Vector3();
	protected materialsEmmisiveData = new Map<
		string,
		{ color: number; intensity: number }
	>();

	constructor(
		protected renderer: ProjectRenderer,
		creatorObject: {
			id: UUID;
			posY: number;
			linkedObjectId: UUID | null;
		},
		object: Object3D
	) {
		this.id = creatorObject.id;
		this._posY = creatorObject.posY / 1000;
		this.linkedObjectId = creatorObject.linkedObjectId;
		this.object = object;

		this.collisionBox.setFromObject(object, true);
		this.collisionBox.getSize(this.dimensions);

		this.collisionBoxHelper = new Box3Helper(this.collisionBox, 0x0000ff);
		if (this.renderer.isDev) this.renderer.scene.add(this.collisionBoxHelper);

		object.traverse((child) => {
			if (!RenderUtils.isMesh(child)) return;

			const material = child.material
				? (child.material as MeshStandardMaterial).clone()
				: new MeshStandardMaterial();

			this.materialsEmmisiveData.set(child.uuid, {
				color: material.emissive.getHex(),
				intensity: material.emissiveIntensity,
			});

			child.material = material;
		});
	}

	set posY(value: number) {
		this._posY = value;

		this.object.position.setY(value);
		this.collisionBox.setFromObject(this.object, true);
		this.renderer.emitter.emit(ThreeEvent.UPDATE_OBJECT_POS_Y, {
			id: this.id,
			posY: Math.round(
				(value + this.renderer.handleManager.originToYCenterConst) * 1000
			),
		});
	}

	get posY() {
		return this._posY;
	}

	get isColliding() {
		return this._isColliding;
	}

	set isColliding(value: boolean) {
		if (this._isColliding === value) return;

		this._isColliding = value;

		this.object.traverse((child) => {
			if (!RenderUtils.isMesh(child)) return;

			const emissiveData = this.materialsEmmisiveData.get(child.uuid);
			if (!emissiveData) return;

			(child.material as MeshStandardMaterial).emissive.set(
				value ? 0xff0000 : new Color(emissiveData.color)
			);
			(child.material as MeshStandardMaterial).emissiveIntensity = value
				? 0.5
				: emissiveData.intensity;
		});
	}

	updateBox3() {
		this.object.updateMatrixWorld();
		this.collisionBox.setFromObject(this.object, true);
	}

	dispose() {
		this.renderer.project.remove(this.object);
		this.collisionBoxHelper.removeFromParent();
		this.collisionBoxHelper.dispose();
		RenderUtils.disposeObject(this.object);
	}
}
